import { useState } from 'react';
import { filter, find, forEach, map, orderBy, sortBy } from 'lodash';
import { SearchInvoiceActivities } from '@/app/features/DebtorPortal/InvoiceDetail/invoiceDetail.types';
import { getCompanyData } from '@/app/shared/constants/companies';
import { LogoType } from '@/app/shared/constants/companies/company.types';
import { useActivityHelpers } from './useActivityHelpers';
import { useActivityDescriptions } from './useActivityDescriptions';

interface TransformedActivity {
    timestamp: Date;
    description: string;
    hasTimestamp: boolean;
    label: string;
    icon: string;
    activityType: string;
}

export const useActivityFilters = (
    activities: SearchInvoiceActivities[] | undefined,
    invoiceNumber: string
) => {
    const { logos } = getCompanyData();
    const [filters, setFilters] = useState<string[]>([]);
    const { getActivityType, getActivityLabel } = useActivityHelpers();
    const { getActivityDescription } = useActivityDescriptions(invoiceNumber);

    const allActivities: TransformedActivity[] = activities?.map((activity) => ({
        timestamp: new Date(activity.date),
        description: getActivityDescription(activity),
        hasTimestamp: true,
        label: getActivityLabel(activity),
        icon: logos[LogoType.SMALL],
        activityType: getActivityType(activity),
    })) ?? [];

    const filteredActivities = filters.length > 0
        ? allActivities.filter((activity) => filters.includes(activity.activityType))
        : allActivities;

    const sortedActivities = orderBy(filteredActivities, ['timestamp'], ['desc']);

    const getActivityTypes = async (_lookup: string): Promise<any> => {
        const activityTypes = new Set<string>();
        forEach(allActivities, (a) => activityTypes.add(a.activityType));

        return map(sortBy(Array.from(activityTypes)), (activityType) => ({
            field: 'activity',
            value: activityType,
            originalValue: activityType,
        }));
    };

    const handleFilterChange = async (_column: string, value: string): Promise<void> => {
        setFilters((curr) => {
            const exists = find(curr, (c) => c === value);
            return exists == null
                ? [...curr, value]
                : [...filter(curr, (c) => c !== value)];
        });
    };

    return {
        sortedActivities,
        getActivityTypes,
        handleFilterChange,
    };
};